import React, { useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { BrokenImage as BrokenIcon } from '@mui/icons-material';
import { thumbnailUrl } from '../utils/media';

/**
 * A post's thumbnail, with a placeholder until the image actually arrives.
 *
 * The images are small now — a 2 MB original comes back at about 31 KB — but small is not
 * instant, and a card whose top is blank for half a second reads as broken rather than
 * loading. The skeleton occupies the exact final height, so nothing shifts when the image
 * replaces it.
 *
 * A failed load shows an icon rather than the browser's broken-image glyph, which is
 * indistinguishable from a bug.
 */
export const PostThumbnail: React.FC<{ src: string; height?: number }> = ({ src, height = 140 }) => {
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');

  if (state === 'error') {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
        }}
      >
        <BrokenIcon />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height }}>
      {state === 'loading' && (
        <Skeleton
          variant="rectangular"
          height={height}
          sx={{ position: 'absolute', inset: 0 }}
        />
      )}
      <Box
        component="img"
        src={thumbnailUrl(src, 320)}
        alt=""
        loading="lazy"
        decoding="async"
        onLoad={() => setState('loaded')}
        onError={() => setState('error')}
        sx={{
          width: '100%',
          height,
          objectFit: 'cover',
          display: 'block',
          // Fade in rather than appearing abruptly under the skeleton.
          opacity: state === 'loaded' ? 1 : 0,
          transition: 'opacity 0.2s',
        }}
      />
    </Box>
  );
};

export default PostThumbnail;
